import {
  AnnotationRead,
  AnnotationUpsert,
  DataSourceEnum,
} from '@lunit-io/ctl-data-server';

import { HOSTS } from 'src/configs/hosts';
import { dataApi, getAccessToken, HTTP_METHODS } from 'src/http';
import { ClientError, ClientErrorCode } from 'src/http/client-error';
import { Label, LocalFinding } from 'src/interfaces';
import { shouldUseV3 } from 'src/utils/apiVersion';
import FindingUtils from 'src/utils/finding';

import { postLog } from './log';

type SyncAnnotationProp = {
  jobId: string | null;
  projectId: string;
  dataSource?: DataSourceEnum;
  findings: LocalFinding[];
  labels: Label[];
  timeSpent: number;
};

/**
 * Sync annotation to the server
 *
 * @param jobId - Job ID
 * @param projectId - Project ID
 * @param dataSource - Data source (determines v2 or v3 API)
 * @param findings - Findings to save
 * @param labels - Labels to save
 * @param timeSpent - Time spent on annotation
 */
export const syncAnnotation = async ({
  jobId,
  projectId,
  dataSource,
  findings,
  labels,
  timeSpent,
}: SyncAnnotationProp): Promise<void> => {
  if (!jobId) {
    throw new ClientError({
      code: ClientErrorCode.INVALID_JOB_ID,
      message: `Given jobId is invalid : ${jobId}`,
    });
  }

  const method = HTTP_METHODS.POST;
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${getAccessToken()}`,
  };

  if (shouldUseV3(dataSource)) {
    // v3 API: POST /v3/projects/{project_id}/jobs/{id}/annotation
    // Type assertion: LocalFinding is compatible with Finding at runtime
    const annotationRequest: AnnotationUpsert = {
      findings: findings.map(
        FindingUtils.removeConfirmedField
      ) as AnnotationUpsert['findings'],
      labels,
      timeSpent,
    };
    const path = `v3/projects/${projectId}/jobs/${jobId}/annotation`;
    const body = JSON.stringify(annotationRequest);

    try {
      await dataApi.post<AnnotationRead>(path, annotationRequest);
    } catch (error) {
      const err = error as ClientError;
      postLog({
        level_name: 'DEBUG',
        method,
        path: `${HOSTS.CTL_DATA_SERVER}/${path}`,
        header: headers,
        status_code: err.originalError?.response?.status,
        request_body: body,
        error_message: err.originalError?.message,
      });
      throw error;
    }
  } else {
    // v2 API: POST /jobs/{id}/annotation/
    const annotationRequest: {
      findings: LocalFinding[];
      labels: Label[];
      timeSpent: number;
    } = {
      findings: findings.map(FindingUtils.removeConfirmedField),
      labels,
      timeSpent,
    };
    const path = `jobs/${jobId}/annotation/`;
    const body = JSON.stringify(annotationRequest);

    try {
      await dataApi.post(path, annotationRequest);
    } catch (error) {
      const err = error as ClientError;
      postLog({
        level_name: 'DEBUG',
        method,
        path: `${HOSTS.CTL_DATA_SERVER}/${path}`,
        header: headers,
        status_code: err.originalError?.response?.status,
        request_body: body,
        error_message: err.originalError?.message,
      });
      throw error;
    }
  }
};
